/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef, EventEmitter,
  forwardRef,
  Input,
  OnChanges, Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {delay, takeUntil} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {isPresent, looseIdentical} from '../../utils/lang';

const CUSTOM_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DropdownSelectComponent),
  multi: true
};

@Component({
  selector: 'gp-dropdown-select',
  providers: [CUSTOM_VALUE_ACCESSOR],
  template: `
    <div class="dropdown"
         [ngClass]="{open: open, inline: inline, dropup: opensUpward, 'hide-toggle': hideToggle}"
         (click-outside)="onClickOutside()">
      <button *ngIf="!withoutButtons"
              class="btn white dropdown-toggle text-left"
              [ngClass]="buttonClasses"
              (click)="open = !open"
              test-id="dropdownButton"
              [disabled]="disabled">
        <span class="direction-ltr d-inline-block text-ellipsis"
              [ngClass]="{'text-muted': valueString === placeholder}"
              [innerHTML]="forceDisplayValue ? forceDisplayValue : valueString"></span>

      </button>
      <div class="dropdown-menu" #dropdownEl [ngClass]="menuClasses" #menu>
        <ng-content></ng-content>
      </div>
    </div>
  `,
  styleUrls: ['./dropdown-select.component.css']
})
export class DropdownSelectComponent extends Destroyable implements ControlValueAccessor, AfterViewInit, DoCheck, OnChanges {

  public optionMap: Map<string, any> = new Map<string, any>();
  public open: boolean = false;

  @ViewChild('dropdownEl', {static: true})
  public dropdownEl: ElementRef;

  @Input()
  public menuClasses: any;

  @Input()
  public inline: boolean = false;

  @Input()
  public withoutButtons: boolean = false;

  @Input()
  public disabled: boolean = false;

  @Input()
  public buttonClasses: any;

  @Input()
  public forceDisplayValue: string;

  public valueString: string;

  @Input()
  public placeholder: string;

  @Input()
  private direction: string = 'down';

  @Input()
  private customPlaceholderGenerator: any;

  @Input()
  public hideToggle = false;

  @Output() public selected: EventEmitter<any> = new EventEmitter();

  private _opensUpward: boolean = false;
  private value: any;

  get opensUpward(): boolean {
    if (this.direction === 'auto') {
      return this._opensUpward;
    }
    return this.direction === 'up';
  }

  constructor(private renderer: Renderer2,
              private translate: TranslateService,
              private elementRef: ElementRef,
              private cd: ChangeDetectorRef) {
    super();
  }

  public ngOnChanges(): any {
    this.valueString = this.getValueAsString(this.value);
  }

  public ngAfterViewInit() {
    setTimeout(() => this.valueString = this.getValueAsString(this.value));

    this.translate.onLangChange
      .asObservable()
      .pipe(
        delay(300),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.valueString = this.getValueAsString(this.value);
        this.cd.detectChanges();
      });

    // Hook dropdown direction update on window scroll
    this.renderer.listen('window', 'scroll', this.updateDropdownDirection.bind(this));
    if (this.withoutButtons) {
      this.open = true;
    }
  }

  public ngDoCheck() {
    // Check dropdown direction update on each change detection cycle
    this.updateDropdownDirection();
  }

  public setValue(value: any): void {
    this.writeValue(value);
    this.onChange(this.value);
    this.selected.emit(this.value);
  }

  public writeValue(value: any): void {
    this.value = value;
    this.valueString = this.getValueAsString(value);
    this.open = false;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  public onClickOutside() {
    if (!this.withoutButtons) {
      this.open = false;
    }
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Update dropdown direction according to position of dropdown within the viewport. If the dropdown is positionned
   * at more than half the viewport, it opens upwards, otherwise, downwards.
   */
  private updateDropdownDirection() {
    if (this.dropdownEl && this.dropdownEl.nativeElement.getBoundingClientRect().top < 0) {
      this._opensUpward = false;
      return;
    }

    if (isPresent(this.elementRef)) {
      const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
      const dropdownOffset = this.elementRef.nativeElement.getBoundingClientRect().top;

      if ((dropdownOffset / viewportHeight) > 0.5) {
        this._opensUpward = true;
        return;
      }
    }
    this._opensUpward = false;
  }

  private getValueAsString(key: string): any {
    for (const id of Array.from(this.optionMap.keys())) {
      if (looseIdentical(id, key)) {
        if (this.customPlaceholderGenerator) {
          return this.customPlaceholderGenerator(id, this.optionMap.get(id));
        }
        return this.optionMap.get(id);
      }
    }
    return this.placeholder || '';
  }

  private onChange = (_: any) => {
    // fake function
  };
  private onTouched = (): void => {
    // fake function
  };
}


